import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Space,
  Typography,
  Button,
  Select,
  DatePicker,
  Tag,
  message,
  Empty,
} from "antd";
import {
  ReloadOutlined,
  ShoppingOutlined,
  DollarOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  CloudDownloadOutlined,
  HistoryOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { formatCompactCurrency } from "./salesDashboardUtils";
import {
  SALES_DUMP_EXPORT_TEMPLATE,
  exportTemplateToCsv,
  exportTemplateToExcel,
  getFilteredRowsFromGrid,
} from "./exportUtils";
import AgGridWithColumnState from "../../../../components/AgGridWithColumnState";
import { getSalesDumpColumns, defaultColDef } from "./salesDashboardColumns";
import {
  getProductOrderDataByFileIds,
  getProductOrderDataSummaryByFileIds,
  listPasRadeSalesDumpDownloads,
  triggerPasRadeSalesDump,
  listPasRadeBrands,
} from "../../../../core/api/report";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const { Text } = Typography;
const { Option } = Select;

const LATEST_KEY = "__latest__";
const SALES_DUMP_CHUNK_RETRIES = 2;

const waitMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withRetries = async (fn, retries = SALES_DUMP_CHUNK_RETRIES) => {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt === retries) break;
      await waitMs(600 * (attempt + 1));
    }
  }
  throw lastError;
};

const MergedSalesDump = ({ brandIds, brandNameMap, dateRange, refreshKey, selectedBrands, isActive }) => {
  const [loading, setLoading] = useState(false);
  const [salesDumpData, setSalesDumpData] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [fetchingPasRade, setFetchingPasRade] = useState(false);
  const [fetchProgress, setFetchProgress] = useState("");

  const [downloads, setDownloads] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(LATEST_KEY);
  const [loadingDownloads, setLoadingDownloads] = useState(false);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [summaryData, setSummaryData] = useState(null);

  const gridRef = useRef(null);
  const loadRunRef = useRef(0);
  const brandNameMapRef = useRef(brandNameMap);
  brandNameMapRef.current = brandNameMap;
  const prevActiveRef = useRef(isActive);
  const columnDefs = useMemo(() => getSalesDumpColumns(), []);

  useEffect(() => {
    if (isActive && !prevActiveRef.current) {
      const t = setTimeout(() => window.dispatchEvent(new Event("resize")), 100);
      return () => clearTimeout(t);
    }
    prevActiveRef.current = isActive;
  }, [isActive]);

  const fetchDownloadsList = useCallback(async (forceLatest = false) => {
    if (!brandIds.length) {
      setDownloads([]);
      setSelectedDate(null);
      setSelectedVersion(LATEST_KEY);
      return;
    }
    setLoadingDownloads(true);
    try {
      const res = await listPasRadeSalesDumpDownloads(brandIds);
      const list = res.data || [];
      setDownloads(list);
      setSelectedDate((currentDate) =>
        list.length > 0 && (forceLatest || !currentDate)
          ? dayjs(list[0].created_at).format("YYYY-MM-DD")
          : currentDate
      );
      setSelectedVersion(LATEST_KEY);
    } catch (err) {
      console.error("Failed to load Sales Dump downloads:", err);
      setDownloads([]);
    } finally {
      setLoadingDownloads(false);
    }
  }, [brandIds]);

  useEffect(() => {
    fetchDownloadsList();
  }, [fetchDownloadsList, refreshKey]);

  const availableDates = useMemo(() => {
    const dateSet = new Set();
    downloads.forEach((d) => dateSet.add(dayjs(d.created_at).format("YYYY-MM-DD")));
    return [...dateSet].sort((a, b) => b.localeCompare(a));
  }, [downloads]);

  const downloadsForDate = useMemo(() => {
    if (!selectedDate) return downloads;
    return downloads.filter((d) => dayjs(d.created_at).format("YYYY-MM-DD") === selectedDate);
  }, [downloads, selectedDate]);

  const versionOptions = useMemo(() => {
    const dateMap = {};
    downloadsForDate.forEach((d) => {
      const key = dayjs(d.created_at).format("YYYY-MM-DD HH:mm");
      if (!dateMap[key]) {
        dateMap[key] = { key, ts: d.created_at, brands: new Set(), files: [] };
      }
      dateMap[key].brands.add(d.brand_name);
      dateMap[key].files.push(d);
    });
    return Object.values(dateMap).sort((a, b) => dayjs(b.ts).diff(dayjs(a.ts)));
  }, [downloadsForDate]);

  const resolvedFileIds = useMemo(() => {
    if (!downloadsForDate.length) return [];

    if (selectedVersion === LATEST_KEY) {
      const latestPerBrand = {};
      downloadsForDate.forEach((d) => {
        if (!latestPerBrand[d.brand_id] || dayjs(d.created_at).isAfter(dayjs(latestPerBrand[d.brand_id].created_at))) {
          latestPerBrand[d.brand_id] = d;
        }
      });
      return Object.values(latestPerBrand).map((d) => d.id);
    }

    const match = versionOptions.find((v) => v.key === selectedVersion);
    if (!match) return [];

    const bestPerBrand = {};
    match.files.forEach((d) => {
      if (!bestPerBrand[d.brand_id] || dayjs(d.created_at).isAfter(dayjs(bestPerBrand[d.brand_id].created_at))) {
        bestPerBrand[d.brand_id] = d;
      }
    });
    return Object.values(bestPerBrand).map((d) => d.id);
  }, [downloadsForDate, selectedVersion, versionOptions]);

  const mapSalesDumpRows = useCallback((rows, offset = 0) => (
    (rows || []).map((item, idx) => {
      const brandId = item.brand_id || item.brand;
      const fallbackId = offset + idx;
      return {
        ...item,
        brand_name: brandNameMapRef.current[brandId] || item.company || "Unknown",
        _id: `dump-${item.id || fallbackId}`,
      };
    })
  ), []);

  const fetchSalesDumpData = useCallback(async () => {
    const runId = Date.now();
    loadRunRef.current = runId;
    if (!resolvedFileIds.length) {
      setSalesDumpData([]);
      setTotalRowCount(0);
      return;
    }

    setLoading(true);
    try {
      const res = await withRetries(() => getProductOrderDataByFileIds(resolvedFileIds));
      if (loadRunRef.current !== runId) return;

      const payload = res.data || [];
      const rows = Array.isArray(payload) ? payload : payload.rows || [];
      const mappedRows = mapSalesDumpRows(rows);
      setSalesDumpData(mappedRows);
      setTotalRowCount(mappedRows.length);
      setLastUpdated(dayjs().format("MMM DD, YYYY HH:mm:ss"));
    } catch (error) {
      console.error("Error fetching sales dump data:", error);
      message.error("Failed to load sales dump data.");
    } finally {
      if (loadRunRef.current === runId) setLoading(false);
    }
  }, [resolvedFileIds, mapSalesDumpRows]);

  useEffect(() => {
    fetchSalesDumpData();
  }, [fetchSalesDumpData]);

  const refreshSalesDumpData = useCallback(() => {
    fetchSalesDumpData();
  }, [fetchSalesDumpData]);

  const fetchSalesDumpSummary = useCallback(async () => {
    if (!resolvedFileIds.length) {
      setSummaryData(null);
      return;
    }
    try {
      const res = await withRetries(() => getProductOrderDataSummaryByFileIds(resolvedFileIds));
      const payload = res.data || {};
      setSummaryData({
        totalRecords: Number(payload.total_records || 0),
        totalOrderSales: Number(payload.total_order_sales || 0),
        totalProductSales: Number(payload.total_product_sales || 0),
        totalRefunds: Number(payload.total_refunds || 0),
        totalCost: Number(payload.total_cost || 0),
        brandBreakdown: payload.brand_breakdown || {},
      });
    } catch (error) {
      console.error("Error fetching sales dump summary:", error);
      setSummaryData(null);
    }
  }, [resolvedFileIds]);

  useEffect(() => {
    fetchSalesDumpSummary();
  }, [fetchSalesDumpSummary]);

  const summary = useMemo(() => {
    const stats = { totalRecords: totalRowCount || salesDumpData.length, totalOrderSales: 0, totalProductSales: 0, totalRefunds: 0, totalCost: 0 };
    salesDumpData.forEach((item) => {
      stats.totalOrderSales += parseFloat(item.order_sale_amt || 0);
      stats.totalProductSales += parseFloat(item.product_sales_amt || 0);
      stats.totalRefunds += parseFloat(item.refund_amount || 0);
      stats.totalCost += parseFloat(item.total_cost || 0);
    });
    if (summaryData) {
      stats.totalRecords = summaryData.totalRecords;
      stats.totalOrderSales = summaryData.totalOrderSales;
      stats.totalProductSales = summaryData.totalProductSales;
      stats.totalRefunds = summaryData.totalRefunds;
      stats.totalCost = summaryData.totalCost;
    }
    return stats;
  }, [salesDumpData, totalRowCount, summaryData]);

  const brandChartRef = useRef(null);
  const brandChartInstance = useRef(null);

  const brandBreakdown = useMemo(() => {
    if (summaryData?.brandBreakdown && Object.keys(summaryData.brandBreakdown).length > 0) {
      return summaryData.brandBreakdown;
    }
    const byBrand = {};
    salesDumpData.forEach((item) => {
      const brand = item.brand_name || "Unknown";
      if (!byBrand[brand]) byBrand[brand] = 0;
      byBrand[brand] += parseFloat(item.order_sale_amt || 0);
    });
    return byBrand;
  }, [salesDumpData, summaryData]);

  useEffect(() => {
    if (!brandChartRef.current || Object.keys(brandBreakdown).length === 0) return;
    if (brandChartInstance.current) brandChartInstance.current.destroy();

    const labels = Object.keys(brandBreakdown);
    const data = labels.map((l) => brandBreakdown[l]);
    const colors = ["#1890ff", "#52c41a", "#722ed1", "#fa8c16", "#eb2f96", "#13c2c2", "#faad14"];

    brandChartInstance.current = new Chart(brandChartRef.current, {
      type: "bar",
      plugins: [ChartDataLabels],
      data: {
        labels,
        datasets: [{
          label: "Order Sales ($)",
          data,
          backgroundColor: labels.map((_, i) => colors[i % colors.length]),
          borderRadius: 6,
          maxBarThickness: 72,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 28, right: 8, left: 8 } },
        plugins: {
          legend: { display: false },
          datalabels: {
            anchor: "end",
            align: "top",
            clamp: true,
            formatter: (val) => formatCompactCurrency(val),
            font: { size: 11, weight: "bold" },
            color: "#334155",
          },
        },
        scales: {
          x: { ticks: { font: { size: 11 }, maxRotation: 0, autoSkip: false } },
          y: { beginAtZero: true, ticks: { callback: (v) => formatCompactCurrency(v) }, grid: { color: "#f0f0f0" } },
        },
      },
    });

    return () => { if (brandChartInstance.current) brandChartInstance.current.destroy(); };
  }, [brandBreakdown]);

  const handleFetchPasRade = useCallback(async () => {
    setFetchingPasRade(true);
    setFetchProgress("Loading brand list...");
    try {
      let brands;
      try {
        const bRes = await listPasRadeBrands();
        brands = (bRes.data || []).filter((b) => brandIds.includes(b.brand_id));
      } catch {
        brands = brandIds.map((id) => ({ brand_id: id, brand_name: brandNameMap[id] || `Brand ${id}` }));
      }

      if (!brands.length) {
        message.warning("No PAS RADE credentials configured for selected brands");
        return;
      }

      let totalRows = 0;
      let successCount = 0;
      const failures = [];

      for (let i = 0; i < brands.length; i++) {
        const b = brands[i];
        setFetchProgress(`Fetching ${b.brand_name} (${i + 1}/${brands.length})...`);
        try {
          const res = await triggerPasRadeSalesDump(b.brand_id);
          const data = res.data || res;
          const rows = data.rows_inserted || 0;
          totalRows += rows;
          if (data.status === "success") successCount++;
          message.success(`${b.brand_name}: ${rows} rows`);
        } catch (err) {
          const detail = err.response?.data?.error || err.message;
          console.error(`PAS RADE fetch failed for ${b.brand_name}:`, err);
          failures.push(`${b.brand_name}: ${detail}`);
          message.error({ content: `${b.brand_name} failed: ${detail}`, duration: 8 });
        }
      }

      setFetchProgress("");
      if (failures.length > 0) {
        message.warning({
          content: `${successCount}/${brands.length} brands succeeded. Failed: ${failures.map((f) => f.split(":")[0]).join(", ")}`,
          duration: 15,
        });
      } else if (totalRows > 0) {
        message.success(`Done! ${successCount}/${brands.length} brands fetched, ${totalRows} total rows`);
      } else {
        message.info("Fetch completed but 0 rows were stored");
      }
      await fetchDownloadsList(true);
    } catch (error) {
      const detail = error.response?.data?.error || error.message;
      message.error(`PAS RADE Sales Dump fetch failed: ${detail}`);
    } finally {
      setFetchingPasRade(false);
      setFetchProgress("");
    }
  }, [brandIds, brandNameMap, fetchDownloadsList]);

  const [exportingExcel, setExportingExcel] = useState(false);

  const handleExportCSV = useCallback(() => {
    if (loading) {
      message.warning("Wait until all rows finish loading");
      return;
    }
    const rows = getFilteredRowsFromGrid(gridRef.current, salesDumpData);
    if (!rows.length) {
      message.warning("No rows to export");
      return;
    }
    exportTemplateToCsv(rows, SALES_DUMP_EXPORT_TEMPLATE, "sales-dump");
    message.success("CSV exported successfully");
  }, [loading, salesDumpData]);

  const handleExportExcel = useCallback(async () => {
    if (loading) {
      message.warning("Wait until all rows finish loading");
      return;
    }
    const rows = getFilteredRowsFromGrid(gridRef.current, salesDumpData);
    if (!rows.length) {
      message.warning("No rows to export");
      return;
    }
    setExportingExcel(true);
    try {
      message.loading({
        content: `Building Excel from ${rows.length.toLocaleString()} loaded rows...`,
        key: "excel-export",
        duration: 0,
      });
      await new Promise((resolve) => setTimeout(resolve, 0));
      await exportTemplateToExcel(rows, SALES_DUMP_EXPORT_TEMPLATE, "sales-dump");
      message.success({ content: "Excel exported successfully", key: "excel-export" });
    } catch (e) {
      console.error(e);
      message.error({ content: e.message || "Excel export failed", key: "excel-export" });
    } finally {
      setExportingExcel(false);
    }
  }, [loading, salesDumpData]);

  const handleExportPDF = useCallback(() => {
    message.warning("PDF export is disabled for full Sales Dump. Use CSV export for complete data.", 5);
  }, []);

  if (!brandIds.length) {
    return <Empty description="Select a client/brand from the global filter to view sales dump data" />;
  }

  return (
    <>
      {/* Toolbar stays usable while the grid loads so Excel is not blocked. */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <Space wrap>
            <Button size="small" icon={<ReloadOutlined />} onClick={refreshSalesDumpData}>Refresh</Button>
            <Button
              size="small"
              icon={<CloudDownloadOutlined />}
              onClick={handleFetchPasRade}
              loading={fetchingPasRade}
              style={{ color: "#eb2f96", borderColor: "#eb2f96" }}
            >
              {fetchProgress || "Fetch PAS RADE Sales Dump"}
            </Button>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleExportCSV} disabled={loading || !salesDumpData.length}>CSV</Button>
            <Button
              size="small"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              loading={exportingExcel}
              disabled={loading || !salesDumpData.length}
              style={{ color: "#52c41a", borderColor: "#52c41a" }}
            >
              {exportingExcel ? "Building Excel..." : "Excel"}
            </Button>
            <Button size="small" icon={<FilePdfOutlined />} onClick={handleExportPDF} disabled={!salesDumpData.length} style={{ color: "#ff4d4f", borderColor: "#ff4d4f" }}>PDF</Button>
          </Space>

          <Space wrap>
            <CalendarOutlined />
            <Text strong style={{ fontSize: 12 }}>Date:</Text>
            <DatePicker
              value={selectedDate ? dayjs(selectedDate) : null}
              onChange={(d) => {
                setSelectedDate(d ? d.format("YYYY-MM-DD") : null);
                setSelectedVersion(LATEST_KEY);
              }}
              disabledDate={(current) => !availableDates.includes(current.format("YYYY-MM-DD"))}
              allowClear={false}
              style={{ width: 140 }}
            />
            <HistoryOutlined />
            <Text strong style={{ fontSize: 12 }}>Version:</Text>
            <Select
              style={{ minWidth: 260 }}
              value={selectedVersion}
              onChange={(val) => setSelectedVersion(val)}
              loading={loadingDownloads}
            >
              <Option value={LATEST_KEY}>
                <Tag color="green" style={{ marginRight: 4 }}>Latest</Tag>
                Most recent on date
              </Option>
              {versionOptions.map((v) => (
                <Option key={v.key} value={v.key}>
                  {dayjs(v.ts).format("h:mm A")}
                  <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>
                    ({[...v.brands].join(", ")})
                  </Text>
                </Option>
              ))}
            </Select>
            {lastUpdated && !loading && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Updated: {lastUpdated}
              </Text>
            )}
          </Space>
        </div>
      </Card>

      <Spin spinning={loading} tip="Loading all sales dump rows...">
      {/* Brand file info */}
      {resolvedFileIds.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Space wrap size={4}>
            <Text type="secondary" style={{ fontSize: 11 }}>Loaded files:</Text>
            {downloads
              .filter((d) => resolvedFileIds.includes(d.id))
              .map((d) => (
                <Tag key={d.id} color="blue" style={{ fontSize: 11 }}>
                  {d.brand_name}: {dayjs(d.created_at).format("MMM DD h:mm A")}
                </Tag>
              ))}
          </Space>
        </div>
      )}

      {/* Summary Statistics */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={5}>
            <Statistic title="Total Records" value={summary.totalRecords} prefix={<ShoppingOutlined />} valueStyle={{ color: "#1890ff" }} />
          </Col>
          <Col span={5}>
            <Statistic title="Order Sales" value={summary.totalOrderSales} prefix={<DollarOutlined />} precision={2} valueStyle={{ color: "#52c41a" }} />
          </Col>
          <Col span={5}>
            <Statistic title="Product Sales" value={summary.totalProductSales} prefix={<DollarOutlined />} precision={2} valueStyle={{ color: "#722ed1" }} />
          </Col>
          <Col span={5}>
            <Statistic title="Refund Amount" value={summary.totalRefunds} prefix={<DollarOutlined />} precision={2} valueStyle={{ color: "#ff4d4f" }} />
          </Col>
          <Col span={4}>
            <Statistic title="Total Cost" value={summary.totalCost} prefix={<DollarOutlined />} precision={2} valueStyle={{ color: "#fa8c16" }} />
          </Col>
        </Row>
      </Card>

      {/* Revenue by Brand Chart */}
      {Object.keys(brandBreakdown).length > 0 && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Card title="Order Sales by Brand" size="small">
              <div style={{ height: 300 }}><canvas ref={brandChartRef} /></div>
            </Card>
          </Col>
        </Row>
      )}

      {/* AG Grid */}
      <Card title="Product Order Data - All Clients" size="small">
        {!resolvedFileIds.length && !loading ? (
          <Empty description={
            downloads.length === 0
              ? "No Sales Dump downloads available. Click 'Fetch PAS RADE Sales Dump' to download."
              : "No data found for the selected version."
          } />
        ) : (
          <div className="ag-theme-alpine" style={{ height: "calc(100vh - 360px)", minHeight: 420, width: "100%" }}>
            <AgGridWithColumnState
              tableId="sales-dashboard-sales-dump"
              gridApiRef={gridRef}
              rowData={salesDumpData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              suppressPaginationPanel={false}
              paginationPageSize={100}
              paginationPageSizeSelector={[50, 100, 250, 500, 1000]}
              enableRangeSelection={true}
              enableCellTextSelection={true}
              suppressMovableColumns={false}
              animateRows={false}
              getRowId={(params) => params.data._id}
              sideBar={{
                toolPanels: [
                  { id: "columns", labelDefault: "Columns", labelKey: "columns", iconKey: "columns", toolPanel: "agColumnsToolPanel" },
                  { id: "filters", labelDefault: "Filters", labelKey: "filters", iconKey: "filter", toolPanel: "agFiltersToolPanel" },
                ],
              }}
            />
          </div>
        )}
      </Card>
      </Spin>
    </>
  );
};

export default MergedSalesDump;
